/**
 * StoryPreviewPane — the floating preview over the story graph (spec 009).
 *
 * Every frame comes from the canonical player (`PlayerHost` /
 * `StoryPlayerHost`), playing the open tabs' unsaved state:
 *
 * - single-scene mode (a scene node selected): `SingleScenePreview`;
 * - runner mode (▶ Start Preview with nothing selected): `StoryRunnerPreview`
 *   from the start node. Whether the runner is active is the story editor's
 *   state (`runnerActive`), so the graph can lock edge editing while it plays.
 *
 * The pop-out window and its BroadcastChannel live in the story editor
 * (`useStoryPreviewPopout`), which stays mounted while scene tabs are open.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { StoryApiResponse, StoryPackageManifest } from '../../../Shared/src/@types';
import type { PlayableSceneInput } from '../../../Shared/src/runtime/playableScene';
import styles from './StoryPreviewPane.module.scss';
import { readStoredSession } from '../api/session';
import { useSceneStore } from '../stores/sceneStore';
import { loadPreviewScene, loadStoryPreviewScenes } from '../utils/previewSources';
import { buildPreviewManifest } from '../utils/previewRunner';
import SingleScenePreview from './preview/SingleScenePreview';
import StoryRunnerPreview, { type RunnerPosition } from './preview/StoryRunnerPreview';

/** Collapsed state of the preview pane, per user (spec 010). */
export const previewCollapsedKey = (userId: number) => `sm.storyPreviewCollapsed.${userId}`;

function readCollapsed(): boolean {
  try {
    const id = readStoredSession()?.id ?? 0;
    return localStorage.getItem(previewCollapsedKey(id)) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean) {
  try {
    const id = readStoredSession()?.id ?? 0;
    localStorage.setItem(previewCollapsedKey(id), collapsed ? '1' : '0');
  } catch {
    /* storage unavailable: the pane just forgets */
  }
}

export type OrientationMode = 'desktop' | 'portrait' | 'landscape';

const ORIENTATION_LABELS: Record<OrientationMode, string> = {
  desktop:   '🖥',
  portrait:  '📱',
  landscape: '📱',
};

/** What the pop-out shows: one scene, or the runner at a scene. */
export type PopOutTarget =
  | { mode: 'scene'; sceneId: number; sceneName: string | null }
  | { mode: 'runner'; storySceneId: number | null };

interface StoryPreviewPaneProps {
  story: StoryApiResponse;
  selectedNodeId: string | null;
  orientation: OrientationMode;
  onOrientationChange: (mode: OrientationMode) => void;
  /** The story runner plays (story editor state, spec 009). */
  runnerActive: boolean;
  onRunnerActiveChange: (active: boolean) => void;
  poppedOut: boolean;
  onPopOut: (target: PopOutTarget) => void;
  onPopBack: () => void;
}

const StoryPreviewPane: React.FC<StoryPreviewPaneProps> = ({
  story,
  selectedNodeId,
  orientation,
  onOrientationChange,
  runnerActive,
  onRunnerActiveChange,
  poppedOut,
  onPopOut,
  onPopBack,
}) => {
  const [collapsed, setCollapsedState] = useState<boolean>(readCollapsed);
  const setCollapsed = useCallback((value: boolean) => {
    setCollapsedState(value);
    writeCollapsed(value);
  }, []);

  // ── Selected node → scene ────────────────────────────────────────────────
  const selected = useMemo(() => {
    if (!selectedNodeId) return null;
    const node = story.graph.nodes.find((n) => n.id === selectedNodeId);
    if (!node || node.type === 'end') return null;
    const ss = story.storyScenes.find((s) => s.id === node.storySceneId);
    return ss ? { sceneId: ss.sceneId, name: ss.sceneName ?? null } : null;
  }, [selectedNodeId, story]);
  const resolvedSceneId = selected?.sceneId ?? null;

  // ── Single-scene source: in-memory tab, draft, else the saved scene ──────
  const [sceneData, setSceneData] = useState<PlayableSceneInput | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!resolvedSceneId) { setSceneData(null); return; }
    let cancelled = false;
    setLoading(true);
    setSceneData(null);
    void loadPreviewScene(resolvedSceneId).then((scene) => {
      if (cancelled) return;
      setSceneData(scene);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [resolvedSceneId]);

  // The open scene changes while it is shown (an edit landing, a save): follow it
  useEffect(() => {
    if (!resolvedSceneId) return;
    return useSceneStore.subscribe(
      (s) => [s.scene, s.keyframes] as const,
      (state) => {
        if (Number(state[0]?.id) !== resolvedSceneId) return;
        void loadPreviewScene(resolvedSceneId).then((scene) => { if (scene) setSceneData(scene); });
      },
      { equalityFn: (a, b) => a[0] === b[0] && a[1] === b[1] },
    );
  }, [resolvedSceneId]);

  // ── Runner ────────────────────────────────────────────────────────────────
  const [manifest, setManifest] = useState<StoryPackageManifest | null>(null);
  const [runnerLoading, setRunnerLoading] = useState(false);
  const [runnerPosition, setRunnerPosition] = useState<RunnerPosition | null>(null);

  useEffect(() => {
    if (!runnerActive) {
      setManifest(null);
      setRunnerPosition(null);
      return;
    }
    let cancelled = false;
    setRunnerLoading(true);
    void loadStoryPreviewScenes(story).then((scenes) => {
      if (cancelled) return;
      setManifest(buildPreviewManifest(story, scenes));
      setRunnerLoading(false);
    });
    return () => { cancelled = true; };
    // The runner plays the story as it was when it started
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runnerActive]);

  const startRunner = useCallback(() => {
    if (!story.graph.startNodeId) return;
    onRunnerActiveChange(true);
  }, [story.graph.startNodeId, onRunnerActiveChange]);

  const stopRunner = useCallback(() => onRunnerActiveChange(false), [onRunnerActiveChange]);

  const handlePopOut = useCallback(() => {
    if (runnerActive) onPopOut({ mode: 'runner', storySceneId: runnerPosition?.storySceneId ?? null });
    else if (resolvedSceneId) onPopOut({ mode: 'scene', sceneId: resolvedSceneId, sceneName: selected?.name ?? null });
  }, [runnerActive, runnerPosition, resolvedSceneId, selected, onPopOut]);

  // ── Render ────────────────────────────────────────────────────────────────
  const renderStoryOverview = () => {
    const hasStart = !!story.graph.startNodeId;
    return (
      <div className={styles.runnerInvite}>
        <div className={styles.runnerInviteIcon}>▶</div>
        <p className={styles.runnerInviteTitle}>Run Story</p>
        <p className={styles.runnerInviteHint}>
          {hasStart
            ? 'Play through the complete story following the graph from the start node.'
            : 'No start node set. Mark a scene as Start in the graph to enable preview.'}
        </p>
        {hasStart && (
          <button className={styles.runnerStartBtn} onClick={startRunner}>
            ▶ Start Preview
          </button>
        )}
      </div>
    );
  };

  if (collapsed) {
    return (
      <button
        className={styles.expandBtn}
        onClick={() => setCollapsed(false)}
        title="Show preview"
        data-testid="preview-expand"
      >
        ◀ Preview
      </button>
    );
  }

  const canPop = runnerActive || !!resolvedSceneId;

  return (
    <div className={styles.previewPane} data-testid="story-preview-pane">
      <div className={styles.header} data-testid="preview-pane">
        <span className={styles.headerTitle}>
          {runnerActive
            ? `Story: ${story.name}`
            : selected?.name
              ? `Preview: ${selected.name}`
              : 'Preview'}
        </span>
        <div className={styles.headerActions}>
          {/* Also while popped out: the pop-out follows the pane's orientation */}
          {canPop && (
            <div className={styles.orientationGroup}>
              {(['desktop', 'portrait', 'landscape'] as OrientationMode[]).map((mode) => (
                <button
                  key={mode}
                  className={`${styles.orientationBtn} ${orientation === mode ? styles.orientationActive : ''}`}
                  onClick={() => onOrientationChange(mode)}
                  title={mode.charAt(0).toUpperCase() + mode.slice(1)}
                  aria-pressed={orientation === mode}
                  data-testid={`preview-orientation-${mode}`}
                >
                  <span className={mode === 'landscape' ? styles.rotatedIcon : undefined}>
                    {ORIENTATION_LABELS[mode]}
                  </span>
                </button>
              ))}
            </div>
          )}
          {poppedOut ? (
            <button className={styles.headerBtn} onClick={onPopBack} title="Pop back">⤶</button>
          ) : (
            canPop && (
              <button className={styles.headerBtn} onClick={handlePopOut} title="Pop out" data-testid="preview-pop-out">⤴</button>
            )
          )}
          <button className={styles.headerBtn} onClick={() => setCollapsed(true)} title="Collapse" aria-label="Collapse preview" data-testid="preview-collapse">✕</button>
        </div>
      </div>

      <div className={styles.content}>
        {poppedOut ? (
          <div className={styles.poppedOutMsg}>
            <div className={styles.poppedOutIcon}>↗</div>
            <p>Preview in external window</p>
            <button className={styles.popBackBtn} onClick={onPopBack}>Pull back</button>
          </div>
        ) : runnerActive ? (
          manifest ? (
            <StoryRunnerPreview
              story={story}
              manifest={manifest}
              orientation={orientation}
              onStop={stopRunner}
              onPosition={setRunnerPosition}
            />
          ) : (
            <div className={styles.canvasWrap}>
              {runnerLoading && <div className={styles.loadingOverlay}><div className={styles.spinner} /></div>}
            </div>
          )
        ) : resolvedSceneId ? (
          <SingleScenePreview scene={sceneData} loading={loading} orientation={orientation} />
        ) : (
          renderStoryOverview()
        )}
      </div>
    </div>
  );
};

export default StoryPreviewPane;
